import React, { useState } from "react";
import { Button, Modal, Alert } from "react-bootstrap";
import { connect } from "react-redux";
import Papa from "papaparse";
import { bulkSubmitNotes } from "../../API/Api";
import { fetchActivities } from "../../redux/FetchActivities/FetchActivitiesActions";
import { successCreateToaster, failureCreateToaster } from "../modal-toasters";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";

const CsvUploadModal = ({
  show,
  onHide,
  user,
  fetchActivities
}) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("Select Your File");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState({
    total: 0,
    success: 0,
    failed: 0
  });
  const [showStats, setShowStats] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // DOMPurify v3+ - use directly, not as constructor

  const handleFileOnchange = (e) => {
    const selectedFile = e.target.files[0];

    if (selectedFile) {
      setFile(selectedFile);
      setFileName(e.target.files[0].name);
      setErrorMessage("");
    }
  };

  const validateCsvData = (data, rawHeaders) => {
    // Strict header validation: must match exactly in order and content
    const expectedHeaders = [
      "organizationName",
      "diversityCategories",
      "interactionCategories",
      "location",
      "title",
      "body",
      "numberOfReferals",
      "numberOfHires",
      "effectiveness"
    ];

    if (!rawHeaders) {
      return `CSV header error: No header row found.`;
    }

    // Find missing columns
    const missing = expectedHeaders.filter(h => !rawHeaders.includes(h));
    // Find extra columns
    const extra = rawHeaders.filter(h => !expectedHeaders.includes(h));
    // Find mismatched columns by position
    const mismatched = rawHeaders
      .map((h, i) => (expectedHeaders[i] && h !== expectedHeaders[i] ? { position: i + 1, expected: expectedHeaders[i], got: h } : null))
      .filter(Boolean);

    if (missing.length || extra.length || mismatched.length) {
      let msg = `CSV header error:`;
      if (missing.length) msg += `\nMissing columns: ${missing.join(", ")}`;
      if (extra.length) msg += `\nExtra columns: ${extra.join(", ")}`;
      if (mismatched.length) {
        msg += `\nMismatched columns:`;
        mismatched.forEach(m => {
          msg += `\n  At position ${m.position}: expected "${m.expected}", got "${m.got}"`;
        });
      }
      return msg;
    }

    const requiredFields = ["organizationName", "diversityCategories", "interactionCategories"];
    const errors = [];
    // Validate each row
    data.forEach((row, index) => {
      requiredFields.forEach(field => {
        if (!row[field] || row[field].trim() === "") {
          errors.push(`Row ${index + 1}: Missing required field '${field}'`);
        }
      });
      // Validate numeric fields
      ["numberOfReferals", "numberOfHires"].forEach(field => {
        if (row[field] && isNaN(Number(row[field]))) {
          errors.push(`Row ${index + 1}: '${field}' must be a number`);
        }
      });
    });
    return errors.length > 0 ? errors.join("\n") : null;
  };

  const convertArrayToString = (value) => {
    if (!value) return "";
    return typeof value === "string" ? value : value.join(",");
  };

  const processUpload = async () => {
    if (!file) {
      setErrorMessage("Please select a CSV file first");
      return;
    }

    if (!file.name.endsWith(".csv")) {
      setErrorMessage("Only CSV files are supported");
      return;
    }

    setIsUploading(true);
    setShowStats(false);
    setUploadStats({ total: 0, success: 0, failed: 0 });

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const { data, errors, meta } = results;
        // Strict header check
        const rawHeaders = meta && meta.fields ? meta.fields : null;
        if (errors.length > 0) {
          setErrorMessage("Error parsing CSV file. Please check the format.");
          setIsUploading(false);
          return;
        }
        const validationError = validateCsvData(data, rawHeaders);
        if (validationError) {
          setErrorMessage(validationError);
          setIsUploading(false);
          return;
        }

        const stats = { total: data.length, success: 0, failed: 0 };
        
        // Process all activities in one batch
        const processedNotes = data.map(row => {
          // Process diversity and interaction categories
          let diversityCategories = row.diversityCategories;
          let interactionCategories = row.interactionCategories;
          
          // Convert to arrays if they are strings
          if (typeof diversityCategories === "string") {
            diversityCategories = diversityCategories.split(",").map(item => item.trim());
          }
          
          if (typeof interactionCategories === "string") {
            interactionCategories = interactionCategories.split(",").map(item => item.trim());
          }
          
          // Sanitize text fields
          const sanitizedBody = DOMPurify.sanitize(row.body || "");
          const sanitizedTitle = DOMPurify.sanitize(row.title || "");
          const sanitizedEffectiveness = DOMPurify.sanitize(row.effectiveness || "");
          
          // Create note object
          return {
            organizationName: row.organizationName,
            diversityCategories: diversityCategories,
            interactionCategories: interactionCategories,
            location: row.location || "",
            title: sanitizedTitle,
            body: sanitizedBody,
            author: user.user.firstName + " " + user.user.lastName,
            company_id: user.user.company.companyId,
            numberOfReferals: row.numberOfReferals || "0",
            numberOfHires: row.numberOfHires || "0",
            effectiveness: sanitizedEffectiveness,
          };
        });
        
        try {
          // Submit all notes in one request
          const result = await bulkSubmitNotes({
            notes: processedNotes,
            token: user.user.jtToken,
            company_id: user.user.company.companyId,
          });
          
          // Update stats with the response
          stats.success = result.successCount || 0;
          stats.failed = result.failureCount || 0;
          
          if (result.failureDetails && result.failureDetails.length > 0) {
            console.error("Failed notes:", result.failureDetails);
          }
        } catch (error) {
          console.error("Error processing bulk upload:", error);
          stats.failed = processedNotes.length;
          stats.success = 0;
        }
        
        setUploadStats(stats);
        setShowStats(true);
        setIsUploading(false);
        
        // Show appropriate toast notification
        if (stats.failed === 0) {
          toast(successCreateToaster, {
            position: toast.POSITION.TOP_CENTER,
          });
        } else if (stats.success === 0) {
          toast(failureCreateToaster, {
            position: toast.POSITION.TOP_CENTER,
          });
        } else {
          toast({
            render: () => (
              <div className="toast-inner toast-success">
                <span>Partially successful upload: {stats.success} succeeded, {stats.failed} failed</span>
              </div>
            ),
          }, {
            position: toast.POSITION.TOP_CENTER,
          });
        }
        
        // Refresh activities list
        fetchActivities(user.user.company.companyId, user.user.jtToken);
      },
      error: (error) => {
        setErrorMessage(`Error reading CSV file: ${error.message}`);
        setIsUploading(false);
      }
    });
  };

  const handleCloseAndReset = () => {
    setFile(null);
    setFileName("Select Your File");
    setErrorMessage("");
    setShowStats(false);
    setIsUploading(false);
    onHide();
  };

  return (
    <Modal
      aria-labelledby="contained-modal-title-vcenter"
      centered
      show={show}
      size="md"
      onHide={handleCloseAndReset}
      id="csvUploadModal"
    >
      <Modal.Header>
        <Modal.Title>Bulk Upload Compliance Activities</Modal.Title>
        <button
          type="button"
          className="close opacity-10"
          data-dismiss="modal"
          aria-label="Close"
          onClick={handleCloseAndReset}
        >
          <span aria-hidden="true">
            <i className="fal fa-times text-15 text-dark-fill"></i>
          </span>
        </button>
      </Modal.Header>
      <Modal.Body>
        {errorMessage && (
          <Alert variant="danger" className="mb-3">
            <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{errorMessage}</pre>
          </Alert>
        )}
        
        {showStats && (
          <Alert variant={uploadStats.failed === 0 ? "success" : uploadStats.success === 0 ? "danger" : "warning"} className="mb-3">
            <p className="mb-0">Upload results:</p>
            <ul className="mb-0">
              <li>Total activities: {uploadStats.total}</li>
              <li>Successfully uploaded: {uploadStats.success}</li>
              <li>Failed: {uploadStats.failed}</li>
            </ul>
          </Alert>
        )}
        
        <div className="custom-file mb-4">
          <input
            type="file"
            className="custom-file-input"
            id="csvFile"
            onChange={handleFileOnchange}
            accept=".csv"
            disabled={isUploading}
          />
          <label className="custom-file-label" htmlFor="csvFile">
            {fileName}
          </label>
        </div>
        
        <div className="mb-3">
          <p style={{ fontSize: "14px" }}>
            * Please upload a CSV file following the template format. Each row will create one activity.
          </p>
          <p style={{ fontSize: "14px" }}>
            * Required fields: Organization Name, Diversity Categories, and Interaction Categories.
          </p>
          <Button 
            onClick={() => {
              // Create and trigger download for the template - just headers
              const template = `organizationName,diversityCategories,interactionCategories,location,title,body,numberOfReferals,numberOfHires,effectiveness`;
              
              const element = document.createElement('a');
              const file = new Blob([template], {type: 'text/csv'});
              element.href = URL.createObjectURL(file);
              element.download = "activity_template.csv";
              document.body.appendChild(element);
              element.click();
              document.body.removeChild(element);
            }}
            variant="outline-primary"
            size="sm"
            className="mb-2"
          >
            Download Template
          </Button>
          <Button 
            onClick={() => {
              // Create and trigger download for the instructions
              const instructions = `Activity Bulk Upload Instructions
=====================================

This document explains how to use the CSV template for bulk uploading activities.

TEMPLATE STRUCTURE
-----------------

The CSV template contains the following columns:

* organizationName: The name of the organization (required)
* diversityCategories: Comma-separated list of diversity categories (required)
* interactionCategories: Comma-separated list of interaction types (required)
* location: Where the activity took place
* title: Title of the activity
* body: Detailed description of the activity
* numberOfReferals: Number of referrals resulting from the activity (numeric)
* numberOfHires: Number of hires resulting from the activity (numeric)
* effectiveness: Description of the activity's effectiveness

VALID DIVERSITY CATEGORIES
-------------------------
African American, Asian, Hispanic Or Latino, Native American, Native Hawaiian Or Pacific Islander, LGBTQ, Female, Older/Experienced, Collegiate/Entry Level, Disability, Veteran, Diversity-Other

VALID INTERACTION CATEGORIES
---------------------------
In-Person Meeting, Virtual Meeting, Career Fair, Virtual Fair, Email, Phone Call, Facility Tour, Sponsorship, Other Recruiting Event, Other

HOW TO USE
----------

1. Download the template CSV file
2. Open it in a spreadsheet program (like Microsoft Excel, Google Sheets, etc.)
3. Fill in your activities data, one activity per row
4. Save the file as CSV
5. Upload the file using the "Bulk Upload" button in the application

EXAMPLE CONTENT
--------------
Your CSV file should look like this when filled out:

organizationName,diversityCategories,interactionCategories,location,title,body,numberOfReferals,numberOfHires,effectiveness
"ACME Corporation","African American,Female","In-Person Meeting,Email","New York, NY","Career Fair Event","Detailed description of the activity...",2,1,"Very effective outreach because..."
"XYZ Organization","Hispanic Or Latino,LGBTQ","Virtual Meeting","Remote","Virtual Recruiting","Another activity description",0,0,"Still evaluating"

NOTES
-----
- For fields that accept multiple values (diversityCategories, interactionCategories), separate each value with a comma and enclose in quotes
- Ensure all required fields are filled in
- Do not modify the header row or column order
- Make sure the CSV file is properly formatted`;
              
              const element = document.createElement('a');
              const file = new Blob([instructions], {type: 'text/plain'});
              element.href = URL.createObjectURL(file);
              element.download = "bulk_upload_instructions.txt";
              document.body.appendChild(element);
              element.click();
              document.body.removeChild(element);
            }}
            variant="outline-secondary"
            size="sm"
            className="mb-2 ml-2"
          >
            Download Instructions
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          className="btn btn-primary"
          onClick={processUpload}
          disabled={!file || isUploading}
          type="button"
        >
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
        <Button
          className="btn"
          style={{
            backgroundColor: "white",
            color: "#599EE4",
            border: "none",
          }}
          onClick={handleCloseAndReset}
          type="button"
          disabled={isUploading}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchActivities: (company_id, token) =>
      dispatch(fetchActivities(company_id, token)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CsvUploadModal);